import type { IColumn } from 'core-components/library/list/types'

import type { ICockpitExportCell, ICockpitExportTable } from '../../../api'
import type { IRevenueAndSobHierarchyNode } from '../../../lib'

import { isExpandableRow, isRowBold } from './getHierarchyDimensionColumns'
import type { IHierarchyControls } from './useHierarchyExpansion'

/**
 * A hierarchy-table column that also states how its cell reads once exported.
 *
 * `exportCell` is written beside the `renderFunction` it mirrors, so the two are read — and
 * changed — together. A column without one is a group header: it spans others through their
 * `parentField` and holds no cells of its own.
 */
export interface IHierarchyExportColumn<T> extends IColumn<T> {
  /** Defaults to `true` for every column but the two dimension columns — see `DIMENSION_FIELDS`. */
  isNumeric?: boolean
  exportCell?: (item: T) => ICockpitExportCell
  /** The header text when `title` alone doesn't produce it — a title that takes i18n params, such
   *  as the fiscal year. Plain keys need nothing here. */
  exportTitle?: string
}

/** The hierarchy's label columns. Every other column these tables draw holds a figure — money, a
 *  share, a growth, a score — so the export states it as numeric without each column saying so. */
const DIMENSION_FIELDS = new Set(['geoDimension', 'productDimension'])

interface IParams<T> {
  columns: IHierarchyExportColumn<T>[]
  /** The rows on screen, in screen order — what the user can actually see. */
  rows: T[]
  controls: IHierarchyControls
  translate: (key: string) => string
}

/**
 * The table as it stands on screen, read off the very columns that draw it.
 *
 * Shared by every cockpit table built on the Geo/Product hierarchy, so a column added to one of
 * them is exported by the same rule that renders it, and a group header becomes the `group` of
 * the columns beneath it rather than an empty column of its own.
 */
export const getHierarchyExportTable = <T extends IRevenueAndSobHierarchyNode>({
  columns,
  rows,
  controls,
  translate,
}: IParams<T>): ICockpitExportTable => {
  const titleByField = new Map(
    columns.map(({ field, title, exportTitle }) => [field, exportTitle ?? translate(title)])
  )
  const cellColumns = columns.flatMap(({ field, parentField, isNumeric, exportCell }) =>
    exportCell ? [{ field, parentField, isNumeric, exportCell }] : []
  )

  return {
    columns: cellColumns.map(({ field, parentField, isNumeric }) => ({
      key: field,
      title: titleByField.get(field) ?? '',
      isNumeric: isNumeric ?? !DIMENSION_FIELDS.has(field),
      /** The dimension columns sit under title-less spacers that only exist to line the two-row
       *  header up — no group at all, rather than an empty one. */
      group: (parentField && titleByField.get(parentField)) || undefined,
    })),
    rows: rows.map((item) => ({
      id: item.rowId,
      parentId: item.parentRowId ?? undefined,
      isTotal: item.parentRowId === null,
      isEmphasized: isRowBold(item, isExpandableRow(controls, item.rowId)),
      cells: Object.fromEntries(
        cellColumns.map((column) => [column.field, column.exportCell(item)])
      ),
    })),
  }
}
